import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Box, Flex, Text, Table, Badge, Card, Grid } from "@radix-ui/themes"
import { restaurantPortalAPI } from "../../../services/restaurantApiClient"
import { useRestaurantAuth } from "../../../contexts/RestaurantAuthContext"
import ReportLayout from "./ReportLayout"
import LoadingSpinner from "../dashboard/LoadingSpinner"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import { TrendingUp, TrendingDown, Menu } from "lucide-react"

interface MenuRanking {
    _id: string
    name: string
    category: string
    price: number
    isAvailable: boolean
    quantitySold: number
    revenue: number
}

interface MenuPopularityData {
    period: { start: string; end: string }
    rankings: MenuRanking[]
}

const MenuPopularityReport: React.FC = () => {
    const { restaurantId } = useParams()
    const { user } = useRestaurantAuth()
    const navigate = useNavigate()
    const [data, setData] = useState<MenuPopularityData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [startDate, setStartDate] = useState<string>(new Date(new Date().setDate(1)).toISOString().split('T')[0])
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0])

    useEffect(() => {
        fetchReport()
    }, [restaurantId, startDate, endDate])

    const fetchReport = async () => {
        try {
            setLoading(true)
            const response = await restaurantPortalAPI.getMenuPopularityReport(restaurantId!, startDate, endDate)
            setData(response.data.data)
            setError(null)
        } catch (err: any) {
            console.error("Failed to fetch menu popularity report", err)
            setError("Failed to load report data.")
        } finally {
            setLoading(false)
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "SDG",
        }).format(amount)
    }

    if (loading && !data) {
        return <LoadingSpinner size="3" text="Calculating Stars & Dogs Menu matrix..." />
    }

    if (error) {
        return (
            <Box p="6" className="text-center">
                <Text color="red">{error}</Text>
                <Box mt="4">
                    <button onClick={() => navigate(-1)} className="text-blue-600 underline">Go Back</button>
                </Box>
            </Box>
        )
    }

    if (!data) return null

    const topItems = data.rankings.slice(0, 3)
    const bottomItems = data.rankings.filter(r => r.quantitySold === 0).length > 0
        ? data.rankings.filter(r => r.quantitySold === 0).slice(0, 3)
        : data.rankings.slice(-3).reverse()

    return (
        <Box>
            <Box className="print:hidden mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <Flex gap="6" align="center">
                    <Flex gap="3" align="center">
                        <Label>Start Date:</Label>
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-40"
                        />
                    </Flex>
                    <Flex gap="3" align="center">
                        <Label>End Date:</Label>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-40"
                        />
                    </Flex>
                </Flex>
            </Box>

            <ReportLayout
                title="Menu Item Volume Popularity"
                subtitle="Itemized Sales Performance (Stars vs Dogs)"
                restaurantName={user?.restaurant?.name}
                dateRange={`${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`}
                onBack={() => navigate(`/restaurant/${restaurantId}/reports`)}
            >
                <Grid columns="2" gap="6" mb="8" className="print:border-b print:border-gray-200 print:pb-6 print:mb-6">
                    <Card className="bg-green-50 dark:bg-green-900/10 print:bg-white print:border print:border-green-300">
                        <Flex gap="2" align="center" mb="2">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                            <Text size="3" weight="bold" color="green" className="uppercase tracking-wider print:text-green-700">Star Performer</Text>
                        </Flex>
                        {topItems.length > 0 ? (
                            <Box>
                                <Text size="6" weight="bold" className="text-green-800 print:text-black block mb-1">
                                    {topItems[0].name}
                                </Text>
                                <Text size="2" color="green" className="print:text-gray-600">
                                    {topItems[0].quantitySold} units driving {formatCurrency(topItems[0].revenue)}
                                </Text>
                            </Box>
                        ) : <Text color="gray">No data</Text>}
                    </Card>

                    <Card className="bg-orange-50 dark:bg-orange-900/10 print:bg-white print:border print:border-orange-300">
                        <Flex gap="2" align="center" mb="2">
                            <TrendingDown className="w-5 h-5 text-orange-600" />
                            <Text size="3" weight="bold" color="orange" className="uppercase tracking-wider print:text-orange-700">Dog (Underperformer)</Text>
                        </Flex>
                        {bottomItems.length > 0 ? (
                            <Box>
                                <Text size="6" weight="bold" className="text-orange-800 print:text-black block mb-1">
                                    {bottomItems[0].name}
                                </Text>
                                <Text size="2" color="orange" className="print:text-gray-600">
                                    {bottomItems[0].quantitySold} units driving {formatCurrency(bottomItems[0].revenue)}
                                </Text>
                            </Box>
                        ) : <Text color="gray">No data</Text>}
                    </Card>
                </Grid>

                <Box>
                    <Flex align="center" justify="between" mb="4" className="border-b pb-2">
                        <Text size="4" weight="bold">Full Menu Performance Matrix</Text>
                        <Badge color="blue" variant="soft" className="print:border">
                            {data.rankings.length} Indexed Items
                        </Badge>
                    </Flex>

                    <Table.Root variant="surface" className="print:border print:border-gray-300">
                        <Table.Header className="print:bg-gray-100">
                            <Table.Row>
                                <Table.ColumnHeaderCell className="w-12 text-center">Rank</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell>Item Name</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="text-right">Unit Price</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="text-right">Volume Sold</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="text-right">Gross Derived</Table.ColumnHeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {data.rankings.map((item, index) => (
                                <Table.Row key={item._id} className={
                                    index < Math.ceil(data.rankings.length * 0.1)
                                        ? "bg-green-50/50 print:bg-white"
                                        : item.quantitySold === 0
                                            ? "bg-red-50/50 print:bg-white text-gray-500"
                                            : ""
                                }>
                                    <Table.Cell className="text-center font-mono font-medium text-gray-500">
                                        {index + 1}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Flex align="center" gap="2">
                                            <Menu className="w-4 h-4 text-gray-400 print:hidden" />
                                            <Text className="font-medium text-gray-900 print:text-black">{item.name}</Text>
                                            {!item.isAvailable && (
                                                <Badge size="1" color="gray" variant="soft" className="print:hidden ml-2">86'd</Badge>
                                            )}
                                        </Flex>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color="gray" variant="surface" className="print:border print:bg-transparent">
                                            {item.category}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell className="text-right text-gray-500">
                                        {formatCurrency(item.price)}
                                    </Table.Cell>
                                    <Table.Cell className="text-right font-bold text-gray-800 print:text-black">
                                        {item.quantitySold}
                                    </Table.Cell>
                                    <Table.Cell className="text-right font-medium text-blue-600 print:text-black">
                                        {formatCurrency(item.revenue)}
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                            {data.rankings.length === 0 && (
                                <Table.Row>
                                    <Table.Cell colSpan={6} className="text-center py-8 text-gray-500">
                                        No active menu items found for this restaurant.
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table.Root>
                </Box>
            </ReportLayout>
        </Box>
    )
}

export default MenuPopularityReport
